import React, { useMemo, useState } from "react";
import {
  AppDialog,
  Badge,
  Button,
  DataTableCard,
  DataTableCell,
  DataTableRow,
  FormField,
  Input,
  LoadingBlock,
  Pagination,
  Select,
} from "../../../../shared/ui/composites";
import { useGetAuditLog, useGetAuditMeta } from "../../api/react-query";
import { formatDateTime } from "../../../../shared/utils/numberLocale";
import { fmtInt } from "../../../../shared/utils/formatters";

const PAGE_SIZE = 50;

const EMPTY_FILTERS = {
  user: "",
  entity: "",
  entity_id: "",
  action: "",
  from: "",
  to: "",
};

const COLUMNS = [
  { key: "created_at", label: "زمان" },
  { key: "user", label: "کاربر" },
  { key: "action", label: "عملیات" },
  { key: "entity", label: "موجودیت" },
  { key: "entity_id", label: "شناسه" },
  { key: "details", label: "جزئیات" },
];

const ACTION_META = {
  INSERT: { label: "ایجاد", variant: "job" },
  UPDATE: { label: "ویرایش", variant: "warning" },
  DELETE: { label: "حذف", variant: "destructive" },
};

const ENTITY_LABELS = {
  settings: "تنظیمات",
  profile: "پرسنل",
  personnel_target: "تارگت پرسنل",
  customer_totals: "تارگت مشتری",
};

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function buildDiff(oldData, newData) {
  const before = isPlainObject(oldData) ? oldData : {};
  const after = isPlainObject(newData) ? newData : {};
  const keys = Array.from(
    new Set([...Object.keys(before), ...Object.keys(after)]),
  ).sort();

  return keys.map((key) => {
    const oldValue = before[key];
    const newValue = after[key];
    return {
      key,
      before: oldValue,
      after: newValue,
      changed: JSON.stringify(oldValue) !== JSON.stringify(newValue),
    };
  });
}

function formatValue(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function ValueCell({ value, tone }) {
  if (value === undefined) {
    return <span className="text-slate-400">—</span>;
  }
  if (value === null) {
    return <span className="font-mono text-xs text-slate-400">null</span>;
  }

  const text = formatValue(value);
  const toneClass = tone === "old" ? "text-rose-700" : "text-emerald-700";
  const longValue = text.length > 80 || text.includes("\n");

  if (longValue) {
    return (
      <pre
        dir="ltr"
        className={`max-h-56 overflow-auto whitespace-pre-wrap break-all text-left font-mono text-xs ${toneClass}`}
      >
        {text}
      </pre>
    );
  }

  return (
    <span dir="ltr" className={`break-all font-mono text-xs ${toneClass}`}>
      {text}
    </span>
  );
}

function ActionBadge({ action }) {
  const meta = ACTION_META[action] || { label: action || "—", variant: "muted" };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

function EntityCell({ entity }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-semibold text-slate-800">
        {ENTITY_LABELS[entity] || entity || "—"}
      </span>
      <span dir="ltr" className="font-mono text-[11px] text-slate-400">
        {entity || ""}
      </span>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return formatDateTime(value);
  } catch (_) {
    return String(value);
  }
}

export default function AuditPage() {
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState({ ...EMPTY_FILTERS });
  const [applied, setApplied] = useState({ ...EMPTY_FILTERS });
  const [selected, setSelected] = useState(null);

  const { data: meta } = useGetAuditMeta();

  const query = useMemo(() => {
    const params = { page, pageSize: PAGE_SIZE };
    Object.entries(applied).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params[key] = value;
      }
    });
    return params;
  }, [page, applied]);

  const { data, isLoading, isFetching, refetch } = useGetAuditLog(query);

  const rows = data?.data || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  const updateDraft = (key) => (event) =>
    setDraft((previous) => ({ ...previous, [key]: event.target.value }));

  const submitFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setApplied({ ...draft });
  };

  const clearFilters = () => {
    setDraft({ ...EMPTY_FILTERS });
    setApplied({ ...EMPTY_FILTERS });
    setPage(1);
  };

  const diffRows = useMemo(
    () => (selected ? buildDiff(selected.old_data, selected.new_data) : []),
    [selected],
  );

  return (
    <div className="space-y-4 p-4 md:p-6" dir="rtl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">گزارش رویدادها</h1>
          <p className="mt-1 text-sm text-slate-500">
            ثبت تغییرات ایجاد، ویرایش و حذف روی داده‌های اصلی — همراه با مقادیر
            قبلی و جدید
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          بروزرسانی
        </Button>
      </div>

      <form
        onSubmit={submitFilters}
        className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
      >
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-6">
          <FormField label="کاربر" htmlFor="audit-user" layout="inline">
            <Select
              id="audit-user"
              variant="filter"
              className="w-full"
              value={draft.user}
              onChange={updateDraft("user")}
            >
              <option value="">همه کاربران</option>
              {(meta?.users || []).map((user) => (
                <option key={user.user_id} value={user.user_id}>
                  {user.full_name || user.personnel_code || String(user.user_id)}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="موجودیت" htmlFor="audit-entity" layout="inline">
            <Select
              id="audit-entity"
              variant="filter"
              className="w-full"
              value={draft.entity}
              onChange={updateDraft("entity")}
            >
              <option value="">همه</option>
              {(meta?.entities || []).map((entity) => (
                <option key={entity} value={entity}>
                  {ENTITY_LABELS[entity] || entity}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField
            label="شناسه رکورد"
            htmlFor="audit-entity-id"
            layout="inline"
          >
            <Input
              id="audit-entity-id"
              variant="form"
              value={draft.entity_id}
              onChange={updateDraft("entity_id")}
              placeholder="مثلاً settings_key یا uid"
              dir="ltr"
            />
          </FormField>

          <FormField label="عملیات" htmlFor="audit-action" layout="inline">
            <Select
              id="audit-action"
              variant="filter"
              className="w-full"
              value={draft.action}
              onChange={updateDraft("action")}
            >
              <option value="">همه</option>
              {(meta?.actions || ["INSERT", "UPDATE", "DELETE"]).map(
                (action) => (
                  <option key={action} value={action}>
                    {ACTION_META[action]?.label || action}
                  </option>
                ),
              )}
            </Select>
          </FormField>

          <FormField label="از تاریخ (میلادی)" htmlFor="audit-from" layout="inline">
            <Input
              id="audit-from"
              type="date"
              variant="form"
              value={draft.from}
              onChange={updateDraft("from")}
              dir="ltr"
            />
          </FormField>

          <FormField label="تا تاریخ (میلادی)" htmlFor="audit-to" layout="inline">
            <Input
              id="audit-to"
              type="date"
              variant="form"
              value={draft.to}
              onChange={updateDraft("to")}
              dir="ltr"
            />
          </FormField>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={clearFilters}>
            پاک کردن
          </Button>
          <Button type="submit" variant="submit" size="sm">
            اعمال فیلتر
          </Button>
        </div>
      </form>

      {isLoading ? (
        <LoadingBlock message="در حال بارگذاری رویدادها..." />
      ) : (
        <div className="overflow-hidden rounded-card border border-border bg-white shadow-card-sm">
          <DataTableCard
            className="rounded-none border-0 shadow-none"
            columns={COLUMNS}
            headerClassName="bg-gradient-to-br from-slate-50 to-muted"
            isEmpty={rows.length === 0}
            emptyMessage="رویدادی با این فیلترها یافت نشد"
            minWidth="980px"
          >
            {rows.map((row) => (
              <DataTableRow key={row.id}>
                <DataTableCell className="text-xs text-slate-500">
                  {formatDate(row.created_at)}
                </DataTableCell>
                <DataTableCell>
                  <div className="flex flex-col">
                    <span className="font-semibold text-slate-800">
                      {row.user_full_name || "سیستم / نامشخص"}
                    </span>
                    <span className="text-[11px] text-slate-400" dir="ltr">
                      {row.user_personnel_code || row.user_id || "—"}
                    </span>
                  </div>
                </DataTableCell>
                <DataTableCell>
                  <ActionBadge action={row.action} />
                </DataTableCell>
                <DataTableCell>
                  <EntityCell entity={row.entity} />
                </DataTableCell>
                <DataTableCell>
                  <span
                    dir="ltr"
                    className="block max-w-[220px] truncate font-mono text-xs text-slate-600"
                    title={row.entity_id || ""}
                  >
                    {row.entity_id || "—"}
                  </span>
                </DataTableCell>
                <DataTableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelected(row)}
                  >
                    مشاهده تغییرات
                  </Button>
                </DataTableCell>
              </DataTableRow>
            ))}
          </DataTableCard>

          <Pagination
            page={page}
            pageCount={totalPages}
            onChange={setPage}
            showPageNumbers
            summary={
              total
                ? `نمایش ${fmtInt((page - 1) * PAGE_SIZE + 1)}–${fmtInt(
                    Math.min(page * PAGE_SIZE, total),
                  )} از ${fmtInt(total)}`
                : "۰ مورد"
            }
          />
        </div>
      )}

      <AppDialog
        open={!!selected}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
        title="تغییرات رکورد"
        size="xl"
        showFooter={false}
      >
        {selected && (
          <div className="space-y-3" dir="rtl">
            <div className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <div className="text-slate-400">زمان</div>
                <div>{formatDate(selected.created_at)}</div>
              </div>
              <div>
                <div className="text-slate-400">کاربر</div>
                <div>
                  {selected.user_full_name ||
                    selected.user_personnel_code ||
                    selected.user_id ||
                    "سیستم / نامشخص"}
                </div>
              </div>
              <div>
                <div className="text-slate-400">موجودیت</div>
                <div>
                  {ENTITY_LABELS[selected.entity] || selected.entity} (
                  <span dir="ltr">{selected.entity}</span>)
                </div>
              </div>
              <div>
                <div className="text-slate-400">شناسه</div>
                <div dir="ltr" className="break-all font-mono">
                  {selected.entity_id || "—"}
                </div>
              </div>
            </div>

            {diffRows.length === 0 ? (
              <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
                داده‌ای برای نمایش نیست. (ممکن است فقط یک مقدار حساس تغییر کرده
                باشد که برای امنیت ذخیره نمی‌شود.)
              </p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table
                  className="w-full text-right text-sm"
                  style={{ minWidth: "760px" }}
                >
                  <thead className="bg-slate-50 text-xs text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-semibold">فیلد</th>
                      <th className="px-3 py-2 font-semibold">قبل</th>
                      <th className="px-3 py-2 font-semibold">بعد</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diffRows.map((diff) => (
                      <tr
                        key={diff.key}
                        className={diff.changed ? "bg-amber-50/70" : "bg-white"}
                      >
                        <td
                          dir="ltr"
                          className="border-t border-slate-100 px-3 py-2 align-top font-mono text-xs text-slate-600"
                        >
                          {diff.key}
                        </td>
                        <td className="border-t border-slate-100 px-3 py-2 align-top">
                          <ValueCell value={diff.before} tone="old" />
                        </td>
                        <td className="border-t border-slate-100 px-3 py-2 align-top">
                          <ValueCell value={diff.after} tone="new" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </AppDialog>
    </div>
  );
}
